import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Download, FileSpreadsheet, Sparkles, PenLine, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE } from '@/utils/api';
import { errText } from './StepsBasic';

const emptyManual = () => ({ categories: [{ name: 'Principales', products: [{ name: '', price: '', description: '' }] }] });

// Revisión editable de la propuesta (manual, CSV/Excel o IA) antes de guardar
const MenuReview = ({ proposal, setProposal }) => {
  const upd = (ci, pi, k, v) => setProposal(p => ({ ...p, categories: p.categories.map((c, i) => i !== ci ? c : pi === null ? { ...c, [k]: v } : { ...c, products: c.products.map((x, j) => j === pi ? { ...x, [k]: v } : x) }) }));
  const addP = (ci) => setProposal(p => ({ ...p, categories: p.categories.map((c, i) => i === ci ? { ...c, products: [...c.products, { name: '', price: '', description: '' }] } : c) }));
  const delP = (ci, pi) => setProposal(p => ({ ...p, categories: p.categories.map((c, i) => i === ci ? { ...c, products: c.products.filter((_, j) => j !== pi) } : c).filter(c => c.products.length) }));
  return (
    <div className="space-y-4 max-h-[50vh] overflow-auto pr-1" data-testid="menu-review">
      {proposal.categories.map((c, ci) => (
        <div key={ci} className="border border-border rounded-2xl p-3 space-y-2">
          <Input value={c.name} onChange={e => upd(ci, null, 'name', e.target.value)} className="font-semibold" data-testid={`review-category-${ci}`} />
          {c.products.map((p, pi) => (
            <div key={pi} className="grid grid-cols-[1fr_110px_36px] gap-2">
              <Input value={p.name} placeholder="Producto" onChange={e => upd(ci, pi, 'name', e.target.value)} data-testid={`review-product-name-${ci}-${pi}`} />
              <Input type="number" min={0} value={p.price} placeholder="Precio" onChange={e => upd(ci, pi, 'price', e.target.value)} data-testid={`review-product-price-${ci}-${pi}`} />
              <Button size="icon" variant="ghost" onClick={() => delP(ci, pi)} data-testid={`review-product-delete-${ci}-${pi}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => addP(ci)} data-testid={`review-add-product-${ci}`}><Plus className="w-4 h-4 mr-1" />Producto</Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setProposal(p => ({ ...p, categories: [...p.categories, { name: 'Nueva categoría', products: [{ name: '', price: '', description: '' }] }] }))} data-testid="review-add-category">
        <Plus className="w-4 h-4 mr-1" />Categoría
      </Button>
    </div>
  );
};

const download = async (format) => {
  const r = await axios.get(`${API_BASE}/onboarding/menu/template`, { params: { format }, responseType: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(r.data);
  a.download = `plantilla_menu.${format}`;
  a.click();
};

export const StepMenu = ({ onDone }) => {
  const [mode, setMode] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  useEffect(() => { axios.get(`${API_BASE}/onboarding/menu/ai-usage`).then(r => setUsage(r.data)).catch(() => {}); }, []);

  const send = async (path, file) => {
    if (file.size > 10 * 1024 * 1024) return toast.error('El archivo supera los 10 MB');
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/menu/${path}`, fd);
      setProposal({ categories: data.categories });
      setErrors(data.errors || []);
      if (data.usage) setUsage(data.usage);
    } catch (e) { toast.error(errText(e, 'No se pudo leer el archivo')); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    const categories = proposal.categories.map(c => ({ name: c.name, products: c.products.filter(p => p.name.trim()).map(p => ({ name: p.name, price: Number(p.price) || 0, description: p.description || null })) })).filter(c => c.products.length);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/menu/confirm`, { categories });
      toast.success(`${data.products_created} productos guardados`);
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  if (proposal) return (
    <div className="space-y-4" data-testid="step-menu-review">
      <p className="text-sm text-muted-foreground">Revisá y corregí antes de guardar. Nada se guarda hasta que confirmes.</p>
      {errors.length > 0 && <div className="text-xs text-amber-700 bg-amber-50 rounded-xl p-2" data-testid="menu-import-errors">{errors.join(' · ')}</div>}
      <MenuReview proposal={proposal} setProposal={setProposal} />
      <div className="flex gap-2">
        <Button variant="outline" className="rounded-full" onClick={() => { setProposal(null); setMode(null); }} data-testid="menu-review-back">Volver</Button>
        <Button className="rounded-full" onClick={confirm} disabled={busy} data-testid="menu-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirmar y guardar</Button>
      </div>
    </div>
  );

  const Opt = ({ k, Icon, title, text }) => (
    <button onClick={() => (k === 'manual' ? setProposal(emptyManual()) : setMode(k))} data-testid={`menu-mode-${k}`}
      className={`text-left p-4 rounded-2xl border transition-colors ${mode === k ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}>
      <Icon className="w-5 h-5 mb-2 text-primary" /><div className="font-semibold text-sm">{title}</div><div className="text-xs text-muted-foreground">{text}</div>
    </button>
  );
  return (
    <div className="space-y-5" data-testid="step-menu">
      <div className="grid sm:grid-cols-3 gap-3">
        <Opt k="manual" Icon={PenLine} title="Cargar a mano" text="Categorías, productos y precios" />
        <Opt k="file" Icon={FileSpreadsheet} title="CSV o Excel" text="Con nuestra plantilla" />
        <Opt k="ai" Icon={Sparkles} title="Foto o PDF de la carta" text="La IA propone y vos revisás" />
      </div>
      {mode === 'file' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => download('xlsx')} data-testid="download-template-xlsx"><Download className="w-4 h-4 mr-1" />Plantilla Excel</Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => download('csv')} data-testid="download-template-csv"><Download className="w-4 h-4 mr-1" />Plantilla CSV</Button>
          </div>
          <Input type="file" accept=".csv,.xlsx" onChange={e => e.target.files?.[0] && send('parse', e.target.files[0])} data-testid="menu-file-input" />
        </div>
      )}
      {mode === 'ai' && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" data-testid="ai-usage-text">
            {usage ? `Te quedan ${usage.remaining} de ${usage.limit} lecturas con IA (${usage.period === 'prueba' ? 'durante la prueba' : 'este mes'}). Máx. 10 MB.` : 'Máx. 10 MB.'}
          </p>
          <Input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" disabled={busy || usage?.remaining === 0} onChange={e => e.target.files?.[0] && send('ai', e.target.files[0])} data-testid="menu-ai-file-input" />
        </div>
      )}
      {busy && <p className="text-sm flex items-center gap-2" data-testid="menu-reading"><Loader2 className="w-4 h-4 animate-spin" />Leyendo la carta…</p>}
    </div>
  );
};
